// Layer 3 (Transformation): Rule set view (#270, paul-fmea.A3) — projects the
// live rule set onto Paul's V8 Met Diagnostics workbook shape: ten `▶`
// sections, in his order and names, every row's coverage stated by counting
// `MET_DIAGNOSTICS_CAUSES` against the live and proposed rules'
// `sourceCauseId`, never typed. A cause is one of exactly three states —
// `covered` (a live rule names it), `proposed` (a proposed rule awaiting
// approval names it), `gap` (neither) — the same three states and the same
// words the retrieval thread uses (rule-set-retrieval.ts), per the ruling
// that closed review MUST-FIX M2/M8: this vocabulary is not two-state
// anywhere. Pure derivation only: no React, no engine mutation.
// See docs/logic/advisory/rule-set.md, docs/engines/rule-set.md.

import type { Case } from '@/versions/v1/schema/case';
import type { Claim, ResolvedVersion } from '@/versions/v1/schema/claim';
import type { DiagnosticRule } from '@/versions/v1/schema/diagnostic-rule';
import type { Diagnosis } from '@/versions/v1/shared/DiagnosisEngine';
import type {
  MetDiagnosticsCause,
  MetDiagnosticsLikelihood,
  MetDiagnosticsPriority,
  MetDiagnosticsSection,
} from '@/versions/v1/schema/met-diagnostics-cause';
import {
  AFFECTS_FLAG_CODE,
  MET_DIAGNOSTICS_SECTION_ORDER,
  MET_DIAGNOSTICS_SECTION_TARGET,
} from '@/versions/v1/schema/met-diagnostics-cause';
import { clauseWeightLines } from '@/versions/v1/shared/reliability.transform';
import { MET_DIAGNOSTICS_CAUSES } from '@/versions/v1/shared/bunker-hill-met-diagnostics-register';
import { MET_DIAGNOSTICS_GAP_REASONS } from '@/versions/v1/shared/bunker-hill-met-diagnostics-rules';
import { isoDateFromRaisedAt } from '@/versions/v1/shared/clock.derive';
import { DEFAULT_CURSOR } from '@/versions/v1/schema/reading-series';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { RULE_CLAIMS } from '@/versions/v1/shared/bunker-hill-rule-claims';
import { LADDER_BY_RULE_ID } from '@/versions/v1/shared/bunker-hill-tarp-seed';
import {
  bandForSection,
  formatBand,
  liveControlPlanRows,
} from '@/versions/v1/shared/control-plan-band.derive';
import { formatLadderChain, ladderTooltip } from '@/versions/v1/shared/ladder-chain.derive';
import type { ControlPlanRow } from '@/versions/v1/shared/bunker-hill-control-plan-seed';
import { oreZoneAt, ORE_ZONES } from '@/versions/v1/shared/bunker-hill-ore-zones';
import { isOpenCase } from '@/versions/v1/modules/overview/Performance.transform';
import { matchedRuleIdForCase } from '@/versions/v1/shared/RuleSetEngine';
import { projectSidebarHref } from '@/versions/v1/shared/project-sidebar-nav';
import { ruleById } from '@/versions/v1/shared/diagnostic-rule.transform';

/** The three provenance kinds the brief names — never a fourth. A `ruled`-tier
 * claim (e.g. dr-dart-valve's FMR-0006 narrowing) is a case observation later
 * formalised by a ruling, so it buckets as `case-learned`; the claim's own
 * `provenance` string keeps the ruling detail on hover. */
export type RuleSetProvenanceKind = 'workbook' | 'case-learned' | 'paper';

export const PROVENANCE_LABEL: Record<RuleSetProvenanceKind, string> = {
  workbook: 'workbook',
  'case-learned': 'case-learned',
  paper: 'paper',
};

/** One rule naming a cause — a cause row can carry more than one (MD-001 is
 * named by both dr-media and dr-hardness; review M3). */
export type RuleSetRuleEntry = {
  rule: DiagnosticRule;
  provenanceKind: RuleSetProvenanceKind;
  provenanceText: string;
  /** Rival rule diagnosis — the cause this rule rules out when it fires. */
  rivalLabel?: string;
  ladder?: { id: string; chain: string; tooltip: string };
};

export type RuleSetRuleRow = {
  kind: 'rule';
  causeId: string;
  cause: string;
  causeCategory: string;
  affectsCode: string;
  /** Paul's own Likelihood/Priority (#271, paul-fmea.A4) — the cause's, never
   * the rule's. See `MetDiagnosticsLikelihood`/`MetDiagnosticsPriority` for
   * the RulePriority (`P1`–`P4`) collision this is not. */
  workbookLikelihood: MetDiagnosticsLikelihood;
  workbookPriority: MetDiagnosticsPriority;
  rules: readonly RuleSetRuleEntry[];
};

/** A cause named only by a rule whose claim is proposed, not live — review M2:
 * this state must render distinctly from both a live row and a gap. */
export type RuleSetProposedRow = {
  kind: 'proposed';
  causeId: string;
  cause: string;
  causeCategory: string;
  affectsCode: string;
  workbookLikelihood: MetDiagnosticsLikelihood;
  workbookPriority: MetDiagnosticsPriority;
  rules: readonly RuleSetRuleEntry[];
};

export type RuleSetGapRow = {
  kind: 'gap';
  causeId: string;
  cause: string;
  causeCategory: string;
  workbookLikelihood: MetDiagnosticsLikelihood;
  workbookPriority: MetDiagnosticsPriority;
  gapReason: string;
};

export type RuleSetSectionRow = RuleSetRuleRow | RuleSetProposedRow | RuleSetGapRow;

export type RuleSetSection = {
  section: MetDiagnosticsSection;
  target: string;
  bandLine: string | null;
  bandZone: string | null;
  bandFixed: boolean;
  bandTooltip: string | null;
  bandChangeHref: string | null;
  totalCauses: number;
  coveredCount: number;
  proposedCount: number;
  rows: readonly RuleSetSectionRow[];
};

export type EquipmentBandModel = {
  bandLine: string;
  bandTooltip: string;
  fixed: true;
};

export type UngroupedRuleRow = {
  rule: DiagnosticRule;
  provenanceKind: RuleSetProvenanceKind;
  provenanceText: string;
};

export type RuleSetPanelModel = {
  versionChip: string;
  downloadLabel: string;
  totalCauses: number;
  totalCovered: number;
  /** Distinct MD causes named by a rule in `liveRuleSet().proposed` — a
   * narrower figure than `footerProposedCount` below, which counts every
   * pending FMEA change queue-wide (review M1/M5). */
  totalProposed: number;
  coverageLine: string;
  sections: readonly RuleSetSection[];
  /** Live rules with no `symptomGroup` — an equipment/mechanical source, not
   * a direct Met Diagnostics row. Rendered outside the ten sections, never
   * silently folded into one. */
  ungrouped: readonly UngroupedRuleRow[];
  /** All pending FMEA changes awaiting approval, queue-wide — the same count
   * the Approvals tab's version chip shows (`ruleSetCounts(advisory).proposed`,
   * review M5). Deliberately not the same quantity as `totalProposed`: this
   * counts every change in the review queue, not only ones naming a Met
   * Diagnostics cause. */
  footerProposedCount: number;
  equipmentBand: EquipmentBandModel | null;
};

const ZONE_LABEL: Readonly<Record<string, string>> = Object.fromEntries(
  ORE_ZONES.map((zone) => [zone.id, zone.label.split(' · ')[0] ?? zone.label]),
);

const LADDER_RULE_ALIASES: Readonly<Record<string, string>> = {
  'dr-zn-ph-low': 'dr-zn-ph',
  'dr-zn-ph-high': 'dr-zn-ph',
};

function ladderForRule(ruleId: string): RuleSetRuleEntry['ladder'] | undefined {
  const ladder =
    LADDER_BY_RULE_ID.get(ruleId) ??
    LADDER_BY_RULE_ID.get(LADDER_RULE_ALIASES[ruleId] ?? '');
  if (!ladder) return undefined;
  return {
    id: ladder.id,
    chain: formatLadderChain(ladder),
    tooltip: ladderTooltip(ladder),
  };
}

function sectionBandFields(
  section: MetDiagnosticsSection,
  cursor: SeriesTime,
  rows: readonly ControlPlanRow[],
  projectSlug: string,
  bandChangeReviewId?: string,
): Pick<
  RuleSetSection,
  'bandLine' | 'bandZone' | 'bandFixed' | 'bandTooltip' | 'bandChangeHref' | 'target'
> {
  const resolved = bandForSection(section, cursor, rows);
  if (!resolved) {
    return {
      target: MET_DIAGNOSTICS_SECTION_TARGET[section],
      bandLine: null,
      bandZone: null,
      bandFixed: false,
      bandTooltip: null,
      bandChangeHref: null,
    };
  }
  const cursorZone = oreZoneAt(cursor);
  const znBandChangePending =
    bandChangeReviewId && section === 'Zn Recovery Below Target' && resolved.zoneId;
  const zoneLabel = resolved.zoneId
    ? znBandChangePending && cursorZone
      ? cursorZone.label.split(' · ')[0] ?? cursorZone.label
      : ZONE_LABEL[resolved.zoneId] ?? resolved.zoneLabel
    : null;
  return {
    target: formatBand(resolved.band, resolved.unit),
    bandLine: formatBand(resolved.band, resolved.unit),
    bandZone: zoneLabel,
    bandFixed: resolved.fixed,
    bandTooltip: resolved.tooltip,
    bandChangeHref:
      bandChangeReviewId && resolved.row.id === 'cp-zn-rec'
        ? `/projects/${projectSlug}/advisory?tab=approvals&review=${bandChangeReviewId}`
        : null,
  };
}

function provenanceKindFor(claim: Claim<DiagnosticRule>): RuleSetProvenanceKind {
  if (claim.source.tier === 'document' && claim.source.sourceKind === 'paper') return 'paper';
  if (claim.source.tier === 'document') return 'workbook';
  if (claim.source.tier === 'ruled') return 'case-learned';
  if (claim.source.tier === 'case') return 'case-learned';
  return 'workbook';
}

/** The claim that made `ruleId` live in `version` — the one claim for that
 * subject whose id sits in `version.claimIds` (claimsOfVersion's own test,
 * repeated here rather than imported, to stay a pure read of two arrays). */
function liveClaimFor(
  ruleId: string,
  version: ResolvedVersion,
): Claim<DiagnosticRule> | undefined {
  const ids = new Set(version.claimIds);
  return RULE_CLAIMS.find((claim) => claim.subject.id === ruleId && ids.has(claim.id));
}

/** Every live rule naming `cause` — plural, per review M3: a `.find()` here
 * silently dropped a second covering rule (dr-hardness alongside dr-media on
 * MD-001), which put a real live rule under a header claiming it had none. */
function rivalLabelFor(rule: DiagnosticRule, rules: readonly DiagnosticRule[]): string | undefined {
  if (!rule.rival) return undefined;
  return rules.find((row) => row.id === rule.rival)?.diagnosis ?? rule.rival;
}

function liveEntriesFor(
  cause: MetDiagnosticsCause,
  rules: readonly DiagnosticRule[],
  version: ResolvedVersion,
): RuleSetRuleEntry[] {
  return rules
    .filter((rule) => rule.sourceCauseId?.includes(cause.id))
    .map((rule) => {
      const claim = liveClaimFor(rule.id, version);
      const provenanceKind = claim ? provenanceKindFor(claim) : 'workbook';
      return {
        rule,
        provenanceKind,
        provenanceText: claim?.provenance ?? rule.diagnosis,
        rivalLabel: rivalLabelFor(rule, rules),
        ladder: ladderForRule(rule.id),
      };
    });
}

/** Every proposed (not yet live) claim naming `cause`. */
function proposedEntriesFor(
  cause: MetDiagnosticsCause,
  proposed: readonly Claim<DiagnosticRule>[],
  rules: readonly DiagnosticRule[],
): RuleSetRuleEntry[] {
  const allRules = [
    ...rules,
    ...proposed.map((claim) => claim.value as DiagnosticRule),
  ];
  return proposed
    .filter((claim) => (claim.value as DiagnosticRule).sourceCauseId?.includes(cause.id))
    .map((claim) => {
      const rule = claim.value as DiagnosticRule;
      return {
        rule,
        provenanceKind: provenanceKindFor(claim),
        provenanceText: claim.provenance,
        rivalLabel: rivalLabelFor(rule, allRules),
        ladder: ladderForRule(rule.id),
      };
    });
}

export function ruleSetPanelModel(
  version: ResolvedVersion,
  rules: readonly DiagnosticRule[],
  proposed: readonly Claim<DiagnosticRule>[],
  footerProposedCount: number,
  opts: {
    cursor?: SeriesTime;
    controlPlanRows?: readonly ControlPlanRow[];
    bandChangeReviewId?: string;
    projectSlug?: string;
  } = {},
): RuleSetPanelModel {
  const cursor = opts.cursor ?? DEFAULT_CURSOR;
  const planRows = opts.controlPlanRows ?? liveControlPlanRows();
  const projectSlug = opts.projectSlug ?? 'alpha';
  let totalCovered = 0;
  let totalProposed = 0;

  const sections: RuleSetSection[] = MET_DIAGNOSTICS_SECTION_ORDER.map((section) => {
    const causes = MET_DIAGNOSTICS_CAUSES.filter((row) => row.section === section);
    let coveredCount = 0;
    let proposedCount = 0;
    const rows: RuleSetSectionRow[] = causes.map((cause) => {
      const affectsCode = affectsCodeLine(cause);
      const liveEntries = liveEntriesFor(cause, rules, version);
      if (liveEntries.length > 0) {
        totalCovered += 1;
        coveredCount += 1;
        return {
          kind: 'rule',
          causeId: cause.id,
          cause: cause.cause,
          causeCategory: cause.causeCategory,
          affectsCode,
          workbookLikelihood: cause.likelihood,
          workbookPriority: cause.priority,
          rules: liveEntries,
        };
      }
      const proposedEntries = proposedEntriesFor(cause, proposed, rules);
      if (proposedEntries.length > 0) {
        totalProposed += 1;
        proposedCount += 1;
        return {
          kind: 'proposed',
          causeId: cause.id,
          cause: cause.cause,
          causeCategory: cause.causeCategory,
          affectsCode,
          workbookLikelihood: cause.likelihood,
          workbookPriority: cause.priority,
          rules: proposedEntries,
        };
      }
      return {
        kind: 'gap',
        causeId: cause.id,
        cause: cause.cause,
        causeCategory: cause.causeCategory,
        workbookLikelihood: cause.likelihood,
        workbookPriority: cause.priority,
        gapReason: MET_DIAGNOSTICS_GAP_REASONS[cause.id] ?? 'No continuous signal for a when clause',
      };
    });
    const bandFields = sectionBandFields(
      section,
      cursor,
      planRows,
      projectSlug,
      opts.bandChangeReviewId,
    );
    return {
      section,
      ...bandFields,
      totalCauses: causes.length,
      coveredCount,
      proposedCount,
      rows,
    };
  });

  // Review M3: the equipment/mechanical bucket is rules with no `symptomGroup`
  // at all — never "not already rendered somewhere above." A live rule that
  // does carry a symptomGroup (dr-hardness) belongs under its Met Diagnostics
  // cause, however many sibling rules already cover that cause.
  const ungrouped: UngroupedRuleRow[] = rules
    .filter((rule) => !rule.symptomGroup)
    .map((rule) => {
      const claim = liveClaimFor(rule.id, version);
      const provenanceKind = claim ? provenanceKindFor(claim) : 'case-learned';
      return {
        rule,
        provenanceKind,
        provenanceText: claim?.provenance ?? rule.diagnosis,
      };
    });

  const totalCauses = MET_DIAGNOSTICS_CAUSES.length;

  const isoDate = isoDateFromRaisedAt(version.acceptedAt, DEFAULT_CURSOR);
  const [, month, day] = isoDate.split('-');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const versionDateLabel = `${day} ${monthNames[Number(month) - 1] ?? 'Jun'}`;
  const coverageLine = `Rule set v${version.n} · ${versionDateLabel}`;

  const equipResolved = bandForSection('equipment', cursor, planRows);
  const equipmentBand: EquipmentBandModel | null =
    equipResolved && equipResolved.fixed
      ? {
          bandLine: `${formatBand(equipResolved.band, equipResolved.unit)} · fixed`,
          bandTooltip:
            'Trip limit — never proposed against. Derives from rd-bm-brg-temp.spec.hi.',
          fixed: true,
        }
      : null;

  return {
    versionChip: `Rule set v${version.n}`,
    downloadLabel: `FMEA v${version.n}.xlsx`,
    totalCauses,
    totalCovered,
    totalProposed,
    coverageLine,
    sections,
    ungrouped,
    footerProposedCount,
    equipmentBand,
  };
}

/** A section's coverage bar: the covered and proposed shares of its causes, in
 *  percent of the bar (polish.P28c r1; zero when the section has no causes). */
export function sectionCoverageBar(section: Pick<RuleSetSection, 'totalCauses' | 'coveredCount' | 'proposedCount'>): {
  coveredPct: number;
  proposedPct: number;
} {
  if (section.totalCauses <= 0) return { coveredPct: 0, proposedPct: 0 };
  return {
    coveredPct: (section.coveredCount / section.totalCauses) * 100,
    proposedPct: (section.proposedCount / section.totalCauses) * 100,
  };
}

/** Column M–R code string for a cause row, e.g. "PbR · AgR" — affects flags
 * are authoritative even where a row's own `section` (sheet placement)
 * disagrees, per the workbook quirk (MD-043/048/051/052). */
export function affectsCodeLine(cause: MetDiagnosticsCause): string {
  return cause.affects.map((flag) => AFFECTS_FLAG_CODE[flag]).join(' · ');
}

/** Cause-row workbook readout, e.g. "L4 · #1" — Paul's own Likelihood/Priority
 * figures, bare numbers with no worded anchor (the sheet gives none — see
 * `MetDiagnosticsLikelihood`). Review priority-likelihood M1: an earlier cut
 * read "L4 · P1" one row above `dr-media`'s own `RulePriority` pill (`P1`) —
 * a reader who had just been told there is a workbook figure would read the
 * rule's `[P1] workbook` chip as that figure. The workbook Priority no
 * longer uses the letter `P` at all, so no token on this row can be mistaken
 * for `RulePriority`'s own `P1`–`P4` scale; the row's placement at the cause
 * header (never inside a `<Pill>`, which is `RulePriority`'s own shape)
 * keeps the two further apart still. */
export function workbookFigureLine(cause: {
  workbookLikelihood: MetDiagnosticsLikelihood;
  workbookPriority: MetDiagnosticsPriority;
}): string {
  return `L${cause.workbookLikelihood} · #${cause.workbookPriority}`;
}

/** What-to-trend column — the rule's structured `when[]` clause labels, or
 * the honest absence for a prose-only rule (review M6). */
export function whatToTrendLine(rule: DiagnosticRule): string {
  if (!rule.when || rule.when.length === 0) return '—';
  return rule.when.map((clause) => clause.label).join(' · ');
}

/** Tooltip for clause reliability weighting (tarp.A7). */
export function whatToTrendTooltip(rule: DiagnosticRule): string | undefined {
  const lines = clauseWeightLines(rule);
  if (lines.length === 0) return undefined;
  return lines.map((line) => `${line.label} × ${String(line.factor)}`).join(' · ');
}

/** Lab-check column — the rule's discriminator, or the honest absence
 * (review M6). */
export function labCheckLine(rule: DiagnosticRule): string {
  return rule.discriminator?.label ?? '—';
}

/** First open case whose matched rule names `causeId` — the rule-set row's
 * return link to the case drawer (`?case=<id>`), mirroring the drawer's
 * `matchedRuleCauseHref` out-link to `?tab=rule-set&cause=<MD id>`. */
export function openCaseHrefForCauseId(
  slug: string,
  causeId: string,
  cases: readonly Case[],
  diagnosis?: Diagnosis,
): string | undefined {
  for (const caseItem of cases) {
    if (!isOpenCase(caseItem)) continue;
    const ruleId = matchedRuleIdForCase(caseItem, diagnosis);
    if (!ruleId) continue;
    const rule = ruleById(ruleId);
    const sourceIds = rule?.sourceCauseId;
    if (!sourceIds) continue;
    const ids = Array.isArray(sourceIds) ? sourceIds : [sourceIds];
    if (!ids.includes(causeId)) continue;
    return `${projectSidebarHref(slug, 'advisory')}?case=${caseItem.id}`;
  }
  return undefined;
}
